import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SessionV2Record, StateId, SessionId } from '@/types/v2';
import { handleSingleResult, referenceDataDefaults, assertDevShape } from './queryHelpers';
import { SESSIONS_V2_COLUMNS } from './columnConstants';
import { isSessionV2Record } from '@/types/v2/queryShapes';

/**
 * V2 Session Hooks
 *
 * Sessions represent legislative sessions with biennium support.
 * Sessions can be regular or special, with special sessions linked to their parent.
 *
 * Schema: sessions_v2
 * - id: number (PK)
 * - state_id: number (FK to states)
 * - session_code: string (e.g., "2025_26")
 * - session_type: string ("regular" | "special")
 * - biennium_start_year: number
 * - biennium_end_year: number
 * - start_date: string | null
 * - end_date: string | null
 * - is_current: boolean | null
 * - is_active: boolean | null
 * - display_name: string
 * - parent_session_id: number | null (FK to sessions_v2 for special sessions)
 * - special_session_number: number | null
 */

/**
 * Fetch all sessions for a state
 */
async function fetchSessionsV2(stateId: StateId): Promise<SessionV2Record[]> {
  const { data, error } = await supabase
    .from('sessions_v2')
    .select(SESSIONS_V2_COLUMNS)
    .eq('state_id', stateId)
    .order('biennium_start_year', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch sessions: ${error.message}`);
  }

  assertDevShape(data || [], isSessionV2Record, 'fetchSessionsV2');
  return data || [];
}

/**
 * Fetch the current active session for a state
 * Falls back to most recent session if no current session is marked
 */
async function fetchCurrentSession(stateId: StateId): Promise<SessionV2Record | null> {
  // First try to get session marked as current
  const { data, error } = await supabase
    .from('sessions_v2')
    .select(SESSIONS_V2_COLUMNS)
    .eq('state_id', stateId)
    .eq('is_current', true)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch current session: ${error.message}`);
  }

  if (data) {
    return data;
  }

  // Fall back to most recent session by biennium_start_year
  const { data: fallbackData, error: fallbackError } = await supabase
    .from('sessions_v2')
    .select(SESSIONS_V2_COLUMNS)
    .eq('state_id', stateId)
    .order('biennium_start_year', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (fallbackError) {
    throw new Error(`Failed to fetch fallback session: ${fallbackError.message}`);
  }

  return fallbackData;
}

/**
 * Fetch a single session by ID
 */
async function fetchSessionById(sessionId: SessionId): Promise<SessionV2Record | null> {
  const result = await supabase
    .from('sessions_v2')
    .select(SESSIONS_V2_COLUMNS)
    .eq('id', sessionId)
    .single();

  return handleSingleResult(result, 'session');
}

/**
 * Hook to fetch all sessions for a state
 *
 * @param stateId - The state ID to fetch sessions for
 * @returns Query result with sessions array
 *
 * @example
 * const { data: sessions, isLoading } = useSessionsV2(1);
 */
export function useSessionsV2(stateId: StateId) {
  return useQuery({
    queryKey: ['sessions-v2', stateId],
    queryFn: () => fetchSessionsV2(stateId),
    ...referenceDataDefaults,
    enabled: !!stateId,
  });
}

/**
 * Hook to fetch the current active session for a state
 *
 * @param stateId - The state ID to fetch the current session for
 * @returns Query result with the current session or null
 *
 * @example
 * const { data: currentSession, isLoading } = useCurrentSession(1);
 */
export function useCurrentSession(stateId: StateId) {
  return useQuery({
    queryKey: ['sessions-v2', 'current', stateId],
    queryFn: () => fetchCurrentSession(stateId),
    ...referenceDataDefaults,
    enabled: !!stateId,
  });
}

/**
 * Hook to fetch a single session by ID
 *
 * @param sessionId - The session ID to fetch
 * @returns Query result with the session or null
 *
 * @example
 * const { data: session, isLoading } = useSessionV2(5);
 */
export function useSessionV2(sessionId: SessionId | null) {
  return useQuery({
    queryKey: ['sessions-v2', 'detail', sessionId],
    queryFn: () => fetchSessionById(sessionId!),
    ...referenceDataDefaults,
    enabled: !!sessionId,
  });
}
